import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction, UserStoreService } from '@mediahaven/shared';
import { ApiFragment, Mh2Entity, NavigationLink, RecordStatus, RecordType } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';

@Injectable({ providedIn: 'root' })
export class DeleteActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.DELETE;

    // Services
    private readonly dialog: DialogHelper = inject(DialogHelper);
    private readonly userStoreService: UserStoreService = inject(UserStoreService);
    private readonly recordSandbox: RecordsSandbox = inject(RecordsSandbox);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Delete
     * Available when:
     *  * User has delete rights
     *  * Item is NOT deleted
     *  * Item is not a representation (unless rejected)
     *
     * Disabled when:
     *  * Never
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public userActionBuilder(record: ApiFragment): UserAction {
        const hasDeleteRights: boolean = record.hasDeleteRights();
        const isDeleted: boolean = record.isDeleted();
        const isRepresentation: boolean = record.getRecordType() === RecordType.Representation;

        const isNotRepresentationOrRejected: boolean =
            !isRepresentation || record.getRecordStatus() === RecordStatus.Rejected;

        const availableConditions: boolean[] = [hasDeleteRights, !isDeleted, isNotRepresentationOrRejected];
        const disableConditions: ActionDisabledCondition[] = [];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const message: string = this.getDeleteConfirmationMessage(record);
        const title: string = this.translate('actions.delete');
        const yes: string = this.translate('actions.delete');
        const no: string = this.translate('actions.common.cancel');

        return this.dialog.confirm({ title, message, yes, no }).pipe(
            tap(() => this.initBusyToast(record, actionItemOptions, true)),
            withLatestFrom(this.userStoreService.getCurrentUser()),
            switchMap(([confirm, user]) => this.recordSandbox.deleteRecord(record, user).pipe(take(1))),
            map(() => true)
        );
    }

    private getDeleteConfirmationMessage(record: ApiFragment): string {
        const entity: Mh2Entity = record.getMh2Entity();
        return this.translate(`items.delete-confirm-dialog.${entity}`, { items: 1 });
    }

    // #region Labels

    public override getLabelKey(target: ApiFragment): string {
        return 'actions.delete';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.delete';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        return undefined;
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }

    // #endregion
}
